import { haversineMeters, lerp } from '@/lib/geo';
import { PIN_ICON } from './fleet-icons';
import type { LiveStatus } from '@/types/api';

export interface FleetInput {
  id: string;
  plateNumber: string;
  status: LiveStatus;
  lat: number;
  lng: number;
  speedKph: number;
  updatedAtMs: number;
}

interface Tween {
  input: FleetInput;
  fromLat: number;
  fromLng: number;
  startedAt: number;
  duration: number;
  currentLat: number;
  currentLng: number;
}

/** Below this a "move" is GPS jitter, not travel; snapping avoids a shiver. */
const MIN_MOVE_METERS = 1.5;
/** Above this the device was offline and has jumped; tweening would fly a car
 *  across a city at 40 000 km/h, so we teleport instead. */
const MAX_TWEEN_METERS = 4_000;
const DEFAULT_DURATION_MS = 1_400;

export type FleetFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Point, {
  id: string;
  plate: string;
  status: LiveStatus;
  speed: number;
  icon: string;
  alerting: boolean;
}>;

/**
 * Keeps a tween per vehicle so a position push slides the marker instead of
 * teleporting it. One shared rAF loop drives every vehicle, and the loop stops
 * itself as soon as nothing is still moving.
 */
export class FleetAnimator {
  private tweens = new Map<string, Tween>();

  setTargets(inputs: FleetInput[], now = performance.now()): void {
    const seen = new Set<string>();

    for (const input of inputs) {
      seen.add(input.id);
      const existing = this.tweens.get(input.id);

      if (!existing) {
        this.tweens.set(input.id, {
          input,
          fromLat: input.lat,
          fromLng: input.lng,
          startedAt: now,
          duration: 0,
          currentLat: input.lat,
          currentLng: input.lng,
        });
        continue;
      }

      const moved = haversineMeters(
        { lat: existing.input.lat, lng: existing.input.lng },
        { lat: input.lat, lng: input.lng },
      );

      if (moved < MIN_MOVE_METERS) {
        // Same place: refresh the metadata (status, speed) without restarting.
        existing.input = input;
        continue;
      }

      const teleport = moved > MAX_TWEEN_METERS;
      existing.fromLat = teleport ? input.lat : existing.currentLat;
      existing.fromLng = teleport ? input.lng : existing.currentLng;
      existing.startedAt = now;
      existing.duration = teleport ? 0 : DEFAULT_DURATION_MS;
      existing.input = input;
      if (teleport) {
        existing.currentLat = input.lat;
        existing.currentLng = input.lng;
      }
    }

    for (const id of this.tweens.keys()) {
      if (!seen.has(id)) this.tweens.delete(id);
    }
  }

  /** Advances every tween and returns the collection plus whether to keep going. */
  step(now = performance.now()): { data: FleetFeatureCollection; animating: boolean } {
    let animating = false;
    const features: FleetFeatureCollection['features'] = [];

    for (const tween of this.tweens.values()) {
      const { input } = tween;

      if (tween.duration > 0) {
        const t = Math.min(1, (now - tween.startedAt) / tween.duration);
        // Linear: a vehicle travelling at constant speed should not appear to
        // accelerate between fixes.
        tween.currentLat = lerp(tween.fromLat, input.lat, t);
        tween.currentLng = lerp(tween.fromLng, input.lng, t);
        if (t < 1) animating = true;
        else tween.duration = 0;
      }

      features.push({
        type: 'Feature',
        id: input.id,
        geometry: { type: 'Point', coordinates: [tween.currentLng, tween.currentLat] },
        properties: {
          id: input.id,
          plate: input.plateNumber,
          status: input.status,
          speed: Math.round(input.speedKph),
          icon: PIN_ICON(input.status),
          alerting: input.status === 'alert',
        },
      });
    }

    return { data: { type: 'FeatureCollection', features }, animating };
  }

  /** Every vehicle's currently-drawn position, for screen-space hit testing. */
  entries(): { id: string; lat: number; lng: number }[] {
    const out: { id: string; lat: number; lng: number }[] = [];
    for (const tween of this.tweens.values()) {
      out.push({ id: tween.input.id, lat: tween.currentLat, lng: tween.currentLng });
    }
    return out;
  }

  /** Where a marker is being drawn right now — what an overlay anchors to. */
  positionOf(id: string): { lat: number; lng: number } | null {
    const tween = this.tweens.get(id);
    return tween ? { lat: tween.currentLat, lng: tween.currentLng } : null;
  }

  clear(): void {
    this.tweens.clear();
  }
}

